import {
    ActionType,
    CalendarDate,
    formatDateNoTime,
    FranchiseFeeStructureMode,
    FranchiseTerritoryStatus,
} from "@sp-crm/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ContentFull, ContentHeader } from "components/layout";
import { trpc } from "components/rpc/trpc";
import { CrmTable, CrmTableProps } from "components/table/crm-table";
import { DeleteButton, EditButton } from "components/ui/action-button";
import { Badge } from "components/ui/badge";
import { InlineBanner } from "components/ui/inline-banner";
import { PrimaryButton } from "components/ui/primary-button";
import { SearchInput } from "components/ui/search-input";
import { Spinner } from "components/ui/spinner";
import { useMessage } from "hooks/use-message";
import React from "react";
import { useIsAllowedInWholeTenant } from "store/selectors/hooks";
import {
    basisPointsToPercentInput,
    centsToDollarsInput,
    formatDollars,
    nextFormKey,
} from "./fee-schedule-form";
import { FeeStructureFormData, TerritoryFormPanel } from "./territory-form-panel";
import { FeeStructureSummary, ScheduleEntrySummary, TerritoryRow } from "./types";

function formatFeeStructureSummary(fs: FeeStructureSummary): string {
    const min = formatDollars(fs.currentMinimumCents ?? 0);
    if (fs.mode === FranchiseFeeStructureMode.PercentPlusMinimum) {
        const pct = fs.feePercent != null ? `${fs.feePercent}%` : "0%";
        return `${fs.name}: ${pct} (${min} minimum)`;
    }
    return `${fs.name}: ${min}`;
}

function formatScheduleEntry(
    fs: FeeStructureSummary,
    entry: ScheduleEntrySummary,
): string {
    const fee = formatDollars(entry.amountCents);
    const date = formatDateNoTime(entry.effectiveDate);
    if (
        fs.mode === FranchiseFeeStructureMode.PercentPlusMinimum &&
        fs.feePercent != null
    ) {
        return `${fs.feePercent}% (${fee} minimum) on ${date}`;
    }
    return `${fee} on ${date}`;
}

function futureScheduleEntries(
    fs: FeeStructureSummary,
    today: string,
): ScheduleEntrySummary[] {
    return (fs.scheduleEntries ?? []).filter(e => e.effectiveDate > today);
}

export const FranchiseSettingsTerritories: React.FC<unknown> = () => {
    const canCreate = useIsAllowedInWholeTenant(ActionType.CreateFranchiseTerritory);
    const canUpdate = useIsAllowedInWholeTenant(ActionType.UpdateFranchiseTerritory);
    const canDelete = useIsAllowedInWholeTenant(ActionType.DeleteFranchiseTerritory);

    const trpcClient = trpc.useTRPC();
    const {
        data: territories,
        isLoading,
        refetch,
    } = useQuery(trpcClient.franchiseTerritory.list.queryOptions());
    const { data: defaultFeeStructures, isLoading: isLoadingDefaults } = useQuery(
        trpcClient.franchiseTerritory.listDefaultFeeStructures.queryOptions(),
    );

    const { message, setMessage } = useMessage();
    const [searchText, setSearchText] = React.useState("");

    const deleteMutation = useMutation({
        ...trpcClient.franchiseTerritory.delete.mutationOptions(),
        onSuccess: () => {
            refetch();
        },
        onError: (error: { message?: string }) => {
            setMessage("error", error.message || "Failed to delete territory.");
        },
    });

    const [showPanel, setShowPanel] = React.useState(false);
    const [panelKey, setPanelKey] = React.useState(0);
    const [editingTerritory, setEditingTerritory] = React.useState<
        TerritoryRow | undefined
    >(undefined);

    const defaultInitialFeeStructures: FeeStructureFormData[] = React.useMemo(() => {
        if (!defaultFeeStructures) return [];
        return defaultFeeStructures.map(
            (d): FeeStructureFormData => ({
                _key: nextFormKey(),
                existingId: null,
                name: d.name,
                mode: d.mode,
                feePercentDisplay: basisPointsToPercentInput(d.feePercentBasisPoints),
                commencementDate: null,
                fromDefault: true,
                schedule: {
                    kind: "relative" as const,
                    steps: d.steps.map(s => ({
                        _key: nextFormKey(),
                        monthsAfterCommencement: s.monthsAfterCommencement,
                        amountDollars: centsToDollarsInput(s.amountCents),
                    })),
                },
            }),
        );
    }, [defaultFeeStructures]);

    const editingInitialFeeStructures: FeeStructureFormData[] = React.useMemo(() => {
        if (!editingTerritory) return [];
        return editingTerritory.feeStructures.map(
            (fs): FeeStructureFormData => ({
                _key: fs.id,
                existingId: fs.id,
                name: fs.name,
                mode: fs.mode,
                feePercentDisplay: basisPointsToPercentInput(
                    fs.feePercentBasisPoints ?? null,
                ),
                commencementDate: fs.commencementDate
                    ? CalendarDate.parse(fs.commencementDate).getOrElse(null)
                    : null,
                fromDefault: false,
                schedule: {
                    kind: "absolute" as const,
                    entries: fs.scheduleEntries.map(e => ({
                        _key: e.id,
                        effectiveDate: e.effectiveDate,
                        amountDollars: centsToDollarsInput(e.amountCents),
                    })),
                },
            }),
        );
    }, [editingTerritory]);

    const initialFeeStructures = editingTerritory
        ? editingInitialFeeStructures
        : defaultInitialFeeStructures;

    const handleAdd = React.useCallback(() => {
        setEditingTerritory(undefined);
        setPanelKey(k => k + 1);
        setShowPanel(true);
    }, []);

    const handleEdit = React.useCallback((territory: TerritoryRow) => {
        setEditingTerritory(territory);
        setPanelKey(k => k + 1);
        setShowPanel(true);
    }, []);

    const handleDismiss = React.useCallback(() => {
        setShowPanel(false);
        setEditingTerritory(undefined);
    }, []);

    const handleSuccess = React.useCallback(() => {
        refetch();
    }, [refetch]);

    const MAX_VISIBLE_ZIPS = 5;

    const columns: CrmTableProps<TerritoryRow>["columns"] = React.useMemo(
        () => [
            {
                key: "name",
                header: "Name",
            },
            {
                key: "code",
                header: "Code",
            },
            {
                key: "status",
                header: "Status",
                renderCell: ({ record }) =>
                    record ? (
                        <Badge
                            value={
                                record.status === FranchiseTerritoryStatus.Active
                                    ? "Active"
                                    : "Inactive"
                            }
                            type={
                                record.status === FranchiseTerritoryStatus.Active
                                    ? "success"
                                    : "info"
                            }
                        />
                    ) : (
                        <></>
                    ),
            },
            {
                key: "assignedRegionName",
                header: "Assigned Region",
                renderCell: ({ record }) => <>{record?.assignedRegionName ?? ""}</>,
            },
            {
                key: "zipCodes",
                header: "Zip Codes",
                enableSorting: false,
                renderCell: ({ record }) => {
                    if (!record) return <></>;
                    const zips = record.zipCodes;
                    if (zips.length <= MAX_VISIBLE_ZIPS) {
                        return <span className="text-sm">{zips.join(", ")}</span>;
                    }
                    return (
                        <span className="text-sm">
                            {zips.slice(0, MAX_VISIBLE_ZIPS).join(", ")} +
                            {zips.length - MAX_VISIBLE_ZIPS} more
                        </span>
                    );
                },
            },
            {
                key: "feeStructureSummaries" as keyof TerritoryRow,
                header: "Current fee structures",
                enableSorting: false,
                renderCell: ({ record }) => {
                    if (!record || record.feeStructureSummaries.length === 0) {
                        return <span className="text-sm text-gray-400">None</span>;
                    }
                    const today = CalendarDate.todayNoTime.toString();
                    return (
                        <div className="text-sm leading-snug space-y-1">
                            {record.feeStructureSummaries.map(fs => {
                                const future = futureScheduleEntries(fs, today);
                                return (
                                    <div key={fs.name}>
                                        <div>{formatFeeStructureSummary(fs)}</div>
                                        {future.map(e => (
                                            <div
                                                key={e.effectiveDate}
                                                className="pl-2 text-xs text-gray-400">
                                                {formatScheduleEntry(fs, e)}
                                            </div>
                                        ))}
                                    </div>
                                );
                            })}
                        </div>
                    );
                },
            },
            ...((canUpdate || canDelete
                ? [
                      {
                          key: "_actions" as keyof TerritoryRow,
                          header: "",
                          enableSorting: false,
                          renderCell: ({
                              record,
                          }: {
                              record: TerritoryRow | undefined;
                          }) =>
                              record ? (
                                  <div className="flex items-center space-x-2">
                                      {canUpdate ? (
                                          <EditButton
                                              backgroundColor="bg-white"
                                              onClick={() => handleEdit(record)}
                                          />
                                      ) : null}
                                      {canDelete ? (
                                          <DeleteButton
                                              backgroundColor="bg-white"
                                              confirm={{
                                                  title: "Delete Territory?",
                                                  message: `Are you sure you want to delete territory "${record.code}"?`,
                                                  confirmLabel: "Delete",
                                              }}
                                              onClick={() =>
                                                  deleteMutation.mutate({
                                                      id: record.id,
                                                  })
                                              }
                                          />
                                      ) : null}
                                  </div>
                              ) : null,
                      },
                  ]
                : []) as CrmTableProps<TerritoryRow>["columns"]),
        ],
        [canUpdate, canDelete, handleEdit, deleteMutation],
    );

    const search = searchText.toLowerCase();
    const filteredTerritories = (territories ?? []).filter(
        territory =>
            territory.name.toLowerCase().includes(search) ||
            territory.code.toLowerCase().includes(search) ||
            territory.zipCodes.some(zip => zip.toLowerCase().includes(search)),
    );

    return (
        <div className="space-y-4 lg:space-y-8">
            <ContentFull>
                <ContentHeader>Territories</ContentHeader>
                {isLoading || isLoadingDefaults ? (
                    <Spinner />
                ) : (
                    <div className="space-y-4">
                        {canCreate ? (
                            <div>
                                <PrimaryButton onClick={handleAdd}>
                                    Add Territory
                                </PrimaryButton>
                            </div>
                        ) : null}
                        {message ? (
                            <InlineBanner type={message.type}>
                                {message.content}
                            </InlineBanner>
                        ) : null}
                        {territories && territories.length > 0 ? (
                            <>
                                <div className="max-w-sm">
                                    <SearchInput
                                        value={searchText}
                                        onChange={e => setSearchText(e.target.value)}
                                    />
                                </div>
                                {filteredTerritories.length > 0 ? (
                                    <div className="overflow-hidden">
                                        <CrmTable
                                            columns={columns}
                                            data={filteredTerritories}
                                        />
                                    </div>
                                ) : (
                                    <p className="text-sm text-gray-500">
                                        No territories match your search.
                                    </p>
                                )}
                            </>
                        ) : (
                            <p className="text-sm text-gray-500">
                                No franchise territories have been created yet.
                            </p>
                        )}
                    </div>
                )}
            </ContentFull>
            {showPanel ? (
                <TerritoryFormPanel
                    key={panelKey}
                    onDismiss={handleDismiss}
                    onSuccess={handleSuccess}
                    editingTerritory={editingTerritory}
                    initialFeeStructures={initialFeeStructures}
                />
            ) : null}
        </div>
    );
};
